import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Input, 
  Select, 
  Button, 
  Space, 
  message,
  Typography,
  Avatar,
  Row,
  Col,
  Modal,
  Table,
  Spin
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import PageLayout from '../../components/layout/PageLayout';
import ImageUpload from '../../components/banners/ImageUpload';
import videoService from '../../services/videoService';
import masterService from '../../services/masterService';
import { useUser } from '../../context/UserContext';

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

const AddCategory = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [form] = Form.useForm();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [showsSpinner, setShowsSpinner] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isAd, setIsAd] = useState('N');
  
  // Dropdown data
  const [languages, setLanguages] = useState([]);
  const [tags, setTags] = useState([]);
  const [videoShows, setVideoShows] = useState([]);
  const [tvShows, setTvShows] = useState([]);
  const [seasonsOfSeries, setSeasonsOfSeries] = useState([]);
  
  // Form data
  const [showsList, setShowsList] = useState([]);
  const [showForm, setShowForm] = useState({
    show: null,
    show_type: null,
    season: null
  });

  // Load dropdown data
  useEffect(() => {
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    try {
      setLoading(true);
      
      // Load all dropdown data in parallel
      const [
        languagesRes,
        tagsRes,
        videoShowsRes,
        tvShowsRes
      ] = await Promise.allSettled([
        masterService.getLanguages(),
        masterService.getTags(),
        masterService.getVideoShows(),
        masterService.getTvShows()
      ]);

      // Set languages
      if (languagesRes.status === 'fulfilled' && languagesRes.value.data?.status) {
        setLanguages(languagesRes.value.data.data || []);
      }

      // Set tags
      if (tagsRes.status === 'fulfilled' && tagsRes.value.data?.status) {
        setTags(tagsRes.value.data.data || []);
      }

      // Set video shows
      if (videoShowsRes.status === 'fulfilled' && videoShowsRes.value.data?.status) {
        setVideoShows(videoShowsRes.value.data.data || []);
      }

      // Set TV shows
      if (tvShowsRes.status === 'fulfilled' && tvShowsRes.value.data?.status) {
        setTvShows(tvShowsRes.value.data.data || []);
      }

    } catch (error) {
      console.error('Error loading dropdown data:', error);
      message.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const formData = {
        ...values,
        shows_list: showsList
      };
      
      console.log('Submitting category data:', formData);
      const response = await videoService.addVideoCategory(formData);
      console.log('API Response:', response);
      
      if (response.data && response.data.status && response.data.statusCode === 200) {
        message.success(response.data.message || 'Category added successfully!');
        form.resetFields();
        setShowsList([]);
        setIsAd('N');
        navigate('/categories/category');
      } else {
        message.error(response.data.message || 'Failed to add category');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      message.error('Failed to add category');
    } finally {
      setLoading(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.resetFields();
    setShowsList([]);
    setIsAd('N');
  };

  // Handle go back
  const handleGoBack = () => {
    navigate(-1);
  };

  // Handle number input (only allow numbers)
  const handleNumberInput = (e) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (
      charCode !== 8 && // backspace
      charCode !== 46 && // delete
      charCode !== 37 && // left arrow
      charCode !== 39 && // right arrow
      (charCode < 48 || charCode > 57)
    ) {
      e.preventDefault();
    }
  };

  // Handle is_ad change
  const handleIsAdChange = (value) => {
    setIsAd(value);
  };

  // Handle add show modal
  const handleAddShow = () => {
    setVisible(true);
  };

  // Handle show type change
  const handleShowTypeChange = (value) => {
    setShowForm(prev => ({ ...prev, show_type: value, show: null, season: null }));
    setSeasonsOfSeries([]);
  };

  // Handle show selection change
  const handleShowChange = async (value) => {
    setShowForm(prev => ({ ...prev, show: value, season: null }));
    
    // If it's a series, get seasons
    if (showForm.show_type === 'series' && value) {
      try {
        const show = JSON.parse(value);
        const response = await masterService.getAllSeasonsOfTvShow({
          tv_show_id: show.tv_show_id
        });
        
        if (response.data && response.data.status) {
          setSeasonsOfSeries(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching seasons:', error);
        message.error('Failed to fetch seasons');
      }
    }
  };

  // Handle season change
  const handleSeasonChange = (value) => {
    setShowForm(prev => ({ ...prev, season: value }));
  };

  // Handle submit show
  const handleSubmitShow = () => {
    if (!showForm.show) {
      message.error('Please select a show');
      return;
    }

    const showData = JSON.parse(showForm.show);
    const showId = showForm.show_type === 'movie' 
      ? showData.video_show_id 
      : showData.tv_show_id;
    
    const showTitle = showForm.show_type === 'movie'
      ? showData.video_show_title
      : showData.tv_show_name;

    let seasonId = null;
    let seasonTitle = null;
    if (showForm.season) {
      const season = JSON.parse(showForm.season);
      seasonId = season.season_id;
      seasonTitle = season.season_title;
    }

    // Check for duplicates
    const isDuplicate = showsList.some(item => 
      item.show_id === showId && 
      item.show_type === showForm.show_type && 
      item.season_id === seasonId
    );

    if (isDuplicate) {
      message.error('This show is already added');
      return;
    }

    // Add to shows list
    const newShow = {
      show_id: showId,
      show_title: showTitle,
      show_type: showForm.show_type,
      season_id: seasonId,
      season_title: seasonTitle
    };

    setShowsList(prev => [...prev, newShow]);
    setShowForm({ show: null, show_type: null, season: null });
    setSeasonsOfSeries([]);
    setVisible(false);
  };

  // Handle remove show
  const handleRemoveShow = (index) => {
    setShowsList(prev => prev.filter((_, i) => i !== index));
  };

  // Shows table columns
  const showsColumns = [
    {
      title: 'Order',
      key: 'order',
      width: 80,
      render: (text, record, index) => index + 1
    },
    {
      title: 'Show Title',
      key: 'show_title',
      render: (text, record) => (
        <span>
          {record.show_title}
          {record.season_title ? ` - ${record.season_title}` : ''}
        </span>
      )
    },
    {
      title: 'Show Type',
      dataIndex: 'show_type',
      key: 'show_type',
      width: 120
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      render: (text, record, index) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveShow(index)}
          size="small"
        >
          Remove
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Add New Category</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Add Category"
      desc="Create a new video category"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_ad: 'N',
            home_page: 'Y',
            movie_page: 'Y',
            series_page: 'Y',
            vc_is_active: 'Y'
          }}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="Category Name"
                name="vc_name"
                rules={[
                  { required: true, message: 'Please enter category name' }
                ]}
              >
                <Input placeholder="Enter category name" />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                label="Order"
                name="order"
                rules={[
                  { required: true, message: 'Please enter order' }
                ]}
              >
                <Input 
                  type="number"
                  placeholder="Enter order"
                  onKeyPress={handleNumberInput}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Category Description"
            name="vc_description"
          >
            <TextArea 
              rows={4}
              placeholder="Enter category description"
            />
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="Is Ad"
                name="is_ad"
              >
                <Select onChange={handleIsAdChange}>
                  <Option value="Y">Yes</Option>
                  <Option value="N">No</Option>
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                label="Status"
                name="vc_is_active"
              >
                <Select>
                  <Option value="Y">Active</Option>
                  <Option value="N">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Ad-related fields */}
          {isAd === 'Y' && (
            <>
              <Form.Item
                label="Ad URL"
                name="ad_url"
              >
                <Input placeholder="Enter ad URL" />
              </Form.Item>

              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <ImageUpload
                    label="Upload Card Image"
                    name="card_image"
                    width={300}
                    height={400}
                    imageType="video"
                    required={false}
                  />
                </Col>
                
                <Col span={12}>
                  <ImageUpload
                    label="Upload Banner Image"
                    name="banner_image"
                    width={1280}
                    height={720}
                    imageType="ads"
                    required={false}
                  />
                </Col>
              </Row>
            </>
          )}

          {/* Page visibility options */}
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Form.Item
                label="Show on Home Page"
                name="home_page"
              >
                <Select>
                  <Option value="Y">Yes</Option>
                  <Option value="N">No</Option>
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                label="Show on Movie Page"
                name="movie_page"
              >
                <Select>
                  <Option value="Y">Yes</Option>
                  <Option value="N">No</Option>
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                label="Show on Series Page"
                name="series_page"
              >
                <Select>
                  <Option value="Y">Yes</Option>
                  <Option value="N">No</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="Choose Language"
                name="lang_id"
              >
                <Select placeholder="Please select a language">
                  {languages.map(language => (
                    <Option key={language.lang_id} value={language.lang_id}>
                      {language.lang_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                label="Tag"
                name="tag_id"
              >
                <Select placeholder="Please select Tag">
                  {tags.map(tag => (
                    <Option key={tag.tag_id} value={tag.tag_id}>
                      {tag.tag_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Form Actions */}
          <Form.Item style={{ marginTop: '32px' }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                size="large"
              >
                Add Category
              </Button>
              
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                size="large"
              >
                Reset
              </Button>
              
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleGoBack}
                size="large"
              >
                Go Back
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Shows List - Only show when not an ad */}
      {isAd === 'N' && (
        <Card style={{ marginTop: '24px' }}>
          <Spin spinning={showsSpinner}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <Title level={4} style={{ margin: 0 }}>Shows List</Title>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddShow}
              >
                Add Show
              </Button>
            </div>

            <Table
              columns={showsColumns}
              dataSource={showsList}
              rowKey={(record, index) => index}
              pagination={false}
              size="middle"
              locale={{ emptyText: 'No shows added yet' }}
            />
          </Spin>
        </Card>
      )}

      {/* Add Show Modal */}
      <Modal
        title="Add Show"
        open={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={750}
      >
        <Form layout="vertical">
          <Form.Item label="Show Type">
            <Select
              placeholder="Please select show type"
              value={showForm.show_type}
              onChange={handleShowTypeChange}
            >
              <Option value="movie">Movie</Option>
              <Option value="series">Series</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Select Show">
            {showForm.show_type === 'movie' && (
              <Select
                placeholder="Please select movie"
                value={showForm.show}
                onChange={handleShowChange}
              >
                {videoShows.map(show => (
                  <Option key={show.video_show_id} value={JSON.stringify(show)}>
                    {show.video_show_title}
                  </Option>
                ))}
              </Select>
            )}

            {showForm.show_type === 'series' && (
              <Select
                placeholder="Please select series"
                value={showForm.show}
                onChange={handleShowChange}
              >
                {tvShows.map(show => (
                  <Option key={show.tv_show_id} value={JSON.stringify(show)}>
                    {show.tv_show_name}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>

          {showForm.show_type === 'series' && (
            <Form.Item label="Select Season">
              <Select
                placeholder="Please select season"
                value={showForm.season}
                onChange={handleSeasonChange}
                allowClear
              >
                <Option value={null}>No Season</Option>
                {seasonsOfSeries.map(season => (
                  <Option key={season.season_id} value={JSON.stringify(season)}>
                    {season.season_title}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item style={{ marginTop: '24px', textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" onClick={handleSubmitShow}>
                Add Show
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </PageLayout>
  );
};

export default AddCategory;